import React, {
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useMemo,
} from 'react';
import { OverlayScrollbars } from 'overlayscrollbars';
import type { PartialOptions } from 'overlayscrollbars';
import { st, classes } from './useOverlayScrollbar.st.css';
import { forkRef } from '../../utils/forkRef';

export type UseOverlayScrollbarsParams = {
  /** OverlayScrollbars options. */
  options?: PartialOptions | false | null;
};

export type OverlayScrollbarHost = React.FunctionComponent<
  {
    className?: string;
    children: React.ReactNode;
  } & React.HTMLAttributes<HTMLDivElement>
>;

export type OverlayScrollbarContent = React.ForwardRefExoticComponent<
  React.PropsWithoutRef<React.HTMLAttributes<HTMLDivElement>> &
    React.RefAttributes<HTMLDivElement>
>;

const DEFAULT_OPTIONS: PartialOptions = {
  scrollbars: { autoHide: 'leave', autoHideDelay: 0 },
};

type ContainerRef = HTMLDivElement | null;

/**
 * @param params Parameters for customization.
 * @returns A tuple with two values:
 * The first value is the overlay scrollbar host component.
 * The second value is the overlay scrollbar content component.
 */
export const useOverlayScrollbar = (params?: UseOverlayScrollbarsParams) => {
  const options = useRef<PartialOptions>(params?.options || DEFAULT_OPTIONS);
  const scrollableContainerRef = useRef<ContainerRef>(null);
  const contentRef = useRef<ContainerRef>(null);
  const instanceRef = useRef<OverlayScrollbars | null>(null);

  const initialize = useCallback(
    (target: HTMLElement) => {
      // if already initialized do nothing
      if (OverlayScrollbars.valid(instanceRef.current)) {
        return;
      }

      /**
       * If the environment is test or window is undefined,
       * do not initialize the OverlayScrollbars.
       */
      if (process.env.NODE_ENV === 'test' || typeof window === 'undefined') {
        return;
      }

      instanceRef.current = OverlayScrollbars(
        {
          target,
          elements: {
            content: contentRef.current,
            viewport: contentRef.current,
          },
        },
        options.current,
      );
    },
    [options],
  );

  useEffect(() => {
    if (scrollableContainerRef.current) {
      initialize(scrollableContainerRef.current);
    }
    return () => instanceRef.current?.destroy();
  }, [initialize]);

  const OverlayScrollbarHost: OverlayScrollbarHost = useCallback(
    ({ children, className, ...rest }) => {
      return (
        <div
          data-overlayscrollbars-initialize=""
          className={st(classes.root, className)}
          ref={scrollableContainerRef}
          {...rest}
        >
          {children}
        </div>
      );
    },
    [],
  );

  // this is required in order to use forwardRef together with memoization
  const OverlayScrollbarContent = useMemo<OverlayScrollbarContent>(
    () => forwardOverlayScrollbarContent(contentRef),
    [contentRef],
  );

  return [OverlayScrollbarHost, OverlayScrollbarContent] as const;
};

const forwardOverlayScrollbarContent = (
  contentRef: React.MutableRefObject<ContainerRef>,
): OverlayScrollbarContent =>
  forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ children, ...rest }, ref) => {
      return (
        <div
          data-overlayscrollbars-contents
          ref={forkRef(ref, contentRef)}
          {...rest}
        >
          {children}
        </div>
      );
    },
  );
